export interface FieldMapping {
  selector: string;
  section: 'overview' | 'general-info' | 'milestones' | 'features';
  milestoneName?: string;
  phaseName?: string; // Phased General Info dates or phased milestone fieldRefs
  _phaseNameVariants?: string[]; // Internal: phase name variants for fallback matching
  /** Milestone column key variants (underscore vs space) for milestone alerts */
  _milestoneNameVariants?: string[];
  /**
   * Feature fieldRefs (format `feature.{productId}.{featureId}.{dateType}`, see
   * NOTIFICATIONS_ARCHITECTURE.md ~line 441). `featureId` matches `FeatureRow.id` in
   * Features.tsx; `subfield` is the dateType token (e.g. "ga", "demo.eta", "prd.eta").
   */
  featureId?: string;
  subfield?: string;
}

/**
 * Escapes special CSS selector characters in a string.
 * Characters like ( ) [ ] need to be escaped for use in querySelector.
 */
export const escapeCssSelector = (str: string): string => {
  return str.replace(/([()[\]{}!@#$%^&*+?.<>\\|/~`=])/g, '\\$1');
};

/**
 * Normalizes phase name to match what's used in the DOM.
 * Phase names in fieldRef might have underscores (Private_Beta) but in DOM might have spaces or dashes.
 */
export const normalizePhaseNameForSelector = (phaseName: string): string[] => {
  const variants: string[] = [];

  // 1. Original format (as-is)
  variants.push(phaseName);

  // 2. Replace underscores with spaces (Private_Beta -> Private Beta)
  if (phaseName.includes('_')) {
    variants.push(phaseName.replace(/_/g, ' '));
    variants.push(phaseName.replace(/_/g, '-'));
    variants.push(phaseName.toLowerCase().replace(/_/g, ' '));
    variants.push(phaseName.toLowerCase().replace(/_/g, '-'));
  }

  // 3. Replace spaces with underscores (Private Beta -> Private_Beta)
  if (phaseName.includes(' ')) {
    variants.push(phaseName.replace(/\s/g, '_'));
    variants.push(phaseName.replace(/\s/g, '-'));
    variants.push(phaseName.toLowerCase().replace(/\s/g, '_'));
    variants.push(phaseName.toLowerCase().replace(/\s/g, '-'));
  }

  // 4. Lowercase variants
  variants.push(phaseName.toLowerCase());

  return Array.from(new Set(variants));
};

/**
 * Milestone keys in fieldRef use underscores (e.g. Media_Brainstorm); DOM labels may use spaces.
 */
export const normalizeMilestoneNameForSelector = (
  milestoneName: string
): string[] => {
  const variants: string[] = [];
  variants.push(milestoneName);
  if (milestoneName.includes('_')) {
    variants.push(milestoneName.replace(/_/g, ' '));
    variants.push(milestoneName.replace(/_/g, '-'));
  }
  if (milestoneName.includes(' ') || milestoneName.includes('-')) {
    variants.push(milestoneName.replace(/\s+/g, '_'));
    variants.push(milestoneName.replace(/-/g, '_'));
  }
  variants.push(milestoneName.toLowerCase());
  return Array.from(new Set(variants));
};

/**
 * Maps fieldRef from API to DOM selectors
 *
 * Examples:
 * - "date" → release date or marketing moment date
 * - "generalInfo.date" → general info date field
 * - "milestone.MONTZN.date" → milestone MONTZN's date
 * - "RR1" → milestone RR1
 * - null → headline field (for headline-stale)
 */
export const mapFieldRefToSelector = (
  fieldRef: string | null,
  ruleId: string
): FieldMapping | null => {
  // 1. Headline stale (fieldRef is null)
  if (ruleId === 'headline-stale' || fieldRef === null) {
    return {
      selector: '[data-testid="overview-field-headline"]',
      section: 'overview'
    };
  }

  // 1.5. RSD info incomplete (fieldRef "overview") — highlight the whole Overview section
  // except the Headline field, via the wrapper Overview.tsx renders around every other field
  // (productZen through mvp-status-section). See Overview.tsx:1314.
  if (ruleId === 'rsd-info-incomplete') {
    return {
      selector: '[data-testid="overview-fields-except-headline"]',
      section: 'overview'
    };
  }

  // 2. Release Readiness milestone (milestone name, or Phase.milestone.Name for phased moments)
  if (ruleId === 'release-readiness') {
    const phasedMilestone = fieldRef.match(/^(.+?)\.milestone\.(.+)$/);
    if (phasedMilestone) {
      const [, phaseToken, milestoneToken] = phasedMilestone;
      return {
        selector: '[data-milestone-name-key]',
        section: 'milestones',
        phaseName: phaseToken,
        milestoneName: milestoneToken,
        _phaseNameVariants: normalizePhaseNameForSelector(phaseToken),
        _milestoneNameVariants:
          normalizeMilestoneNameForSelector(milestoneToken)
      };
    }
    const nameVariants = normalizeMilestoneNameForSelector(fieldRef);
    const primary = nameVariants[0];
    return {
      selector: `[data-milestone-name-key="${escapeCssSelector(primary)}"]`,
      section: 'milestones',
      milestoneName: fieldRef,
      _milestoneNameVariants: nameVariants
    };
  }

  // 3. Date past due
  if (ruleId === 'date-past-due') {
    if (!fieldRef) return null;

    // 3.0. Feature date fields (format: "feature.{productId}.{featureId}.{dateType}")
    // Examples: "feature.Illustrator_Desktop_(Ai).6a2a7e9d0043eb550c29209a.ga",
    //           "feature.Photoshop.feat-123.demo.eta"
    // Per BE contract (adobe-roadmap-api/docs/NOTIFICATIONS_ARCHITECTURE.md ~line 441):
    // dateType is one of: ga, demo.eta, prd.eta — a closed set (confirmed at ~line 428). The
    // dateType is matched as a literal suffix (not a wildcard) so productId can safely contain
    // dots (e.g. "feature.A.com.feat-123.ga") without being mis-split; featureId is assumed
    // dot-free, consistent with every observed id shape (Mongo ObjectIds, "feat-123" slugs).
    // Features rows are fetched/rendered dynamically (FeaturesContainer), same as milestones —
    // so we return featureId/subfield for a *declarative* highlight computed by Features.tsx
    // per render (see featureDeclarativeHighlight.ts), rather than a selector consumed by the
    // imperative querySelector-based highlightField() flow below.
    if (fieldRef.startsWith('feature.')) {
      const featureMatch = fieldRef.match(
        /^feature\.(.+)\.([^.]+)\.(ga|demo\.eta|prd\.eta)$/
      );
      if (featureMatch) {
        const [, , featureId, subfield] = featureMatch;
        return {
          selector: `[data-testid="features-row-${escapeCssSelector(
            featureId
          )}"]`,
          section: 'features',
          featureId,
          subfield
        };
      }
      // Unrecognized "feature." shape — fall through to return null at the end rather than
      // guessing a selector.
    }

    // 3.1. Phased General Info dates (format: "{phaseName}.generalInfo.{fieldName}")
    // Examples: "Private_Beta.generalInfo.releaseDate", "GA.generalInfo.marketingMoment",
    //           "ETLA.generalInfo.customerNotificationDate" (Price Changes and Offers)
    const phasedGeneralInfoMatch = fieldRef.match(/^(.+?)\.generalInfo\.(.+)$/);
    if (phasedGeneralInfoMatch) {
      const [, phaseName, fieldName] = phasedGeneralInfoMatch;
      // Valid field names: releaseDate, marketingMoment, effectiveDate, targetStart, customerNotificationDate
      if (
        [
          'releaseDate',
          'marketingMoment',
          'effectiveDate',
          'targetStart',
          'customerNotificationDate'
        ].includes(fieldName)
      ) {
        // Normalize phase name: handle underscores, spaces, and special characters
        // Phase names in fieldRef might be "Private_Beta" but in DOM might be "Private Beta" or "private-beta"
        const normalizedPhaseNames = normalizePhaseNameForSelector(phaseName);

        // Build selector that finds the phase section, then the date field within it
        // We'll try to find the phase section by its structure and then the date field
        // Since phases use data-phase-id, we need to find by phase name content or structure
        // Strategy: Find all date fields, then filter by checking if they're within the correct phase section
        // For now, we'll use a more specific selector that includes the phase context
        const selectors = normalizedPhaseNames.map(normalizedPhase => {
          // Try to find the phase section by looking for phase name in headings/content
          // Then find the date field within that phase's GeneralInfo section
          // Format: [data-phase-id] or section containing phase name, then date field
          return `[data-testid="general-info-date-field-${fieldName}"]`;
        });

        // Return the first selector (most common case)
        // The highlighter will need to check which phase section contains this field
        return {
          selector: selectors[0],
          section: 'general-info',
          phaseName: phaseName, // Store original phase name for reference
          _phaseNameVariants: normalizedPhaseNames // Store variants for fallback
        };
      }
    }

    // 3.2. Non-phased General Info dates (format: "generalInfo.{fieldName}")
    if (fieldRef.startsWith('generalInfo.')) {
      const fieldName = fieldRef.replace('generalInfo.', '');
      // Valid field names: releaseDate, marketingMoment, effectiveDate, targetStart, customerNotificationDate
      if (
        [
          'releaseDate',
          'marketingMoment',
          'effectiveDate',
          'targetStart',
          'customerNotificationDate'
        ].includes(fieldName)
      ) {
        return {
          selector: `[data-testid="general-info-date-field-${fieldName}"]`,
          section: 'general-info'
        };
      }
    }

    // 3.3. Phased milestone dates: "{phaseName}.milestone.{milestoneName}.date"
    const phasedMilestoneDate = fieldRef.match(
      /^(.+?)\.milestone\.(.+)\.date$/
    );
    if (phasedMilestoneDate) {
      const [, phaseToken, milestoneToken] = phasedMilestoneDate;
      return {
        selector: '[data-milestone-field="date"]',
        section: 'milestones',
        phaseName: phaseToken,
        milestoneName: milestoneToken,
        _phaseNameVariants: normalizePhaseNameForSelector(phaseToken),
        _milestoneNameVariants:
          normalizeMilestoneNameForSelector(milestoneToken)
      };
    }

    // 3.4. Non-phased milestone dates: "milestone.{milestoneName}.date" (underscores or spaces in name)
    const milestoneMatch = fieldRef.match(/^milestone\.(.+)\.date$/);
    if (milestoneMatch) {
      const milestoneName = milestoneMatch[1];
      const nameVariants = normalizeMilestoneNameForSelector(milestoneName);
      const primary = nameVariants[0];
      return {
        selector: `[data-milestone-field="date"][data-milestone-name-key="${escapeCssSelector(
          primary
        )}"]`,
        section: 'milestones',
        milestoneName: milestoneName,
        _milestoneNameVariants: nameVariants
      };
    }

    // 3.5. Legacy support (backward compatibility)
    // Simple "date" field
    if (fieldRef === 'date') {
      return {
        selector:
          '[data-testid="general-info-date-field-releaseDate"], [data-testid="general-info-date-field-marketingMoment"]',
        section: 'general-info'
      };
    }

    // General info date (legacy format)
    if (fieldRef === 'generalInfo.date') {
      return {
        selector: '[data-testid="general-info-date-field-releaseDate"]',
        section: 'general-info'
      };
    }
  }

  return null;
};
